import React, { useState, useEffect } from 'react';
import './App.css';
import { useSupabaseAuth } from './hooks/useSupabaseAuth';
import { useSupabaseData } from './hooks/useSupabaseData';
import { runMigrations } from './utils/database';

const generateId = () => Date.now().toString(36) + Math.random().toString(36).substr(2);
const hashPassword = (p) => btoa(p);

const App = () => {
  const [theme, setTheme] = useState(() => localStorage.getItem('homeScoutTheme') || 'light');
  const [activeUser, setActiveUser] = useState(null);
  const { user, loading: authLoading, signIn, signOut } = useSupabaseAuth();

  useEffect(() => { runMigrations(); }, []);
  useEffect(() => { setActiveUser(user); }, [user]);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('homeScoutTheme', newTheme);
  };

  const handleLogin = async (username, password, role) => {
    const result = await signIn(username, password, role);
    if (result.success) return true;
    throw new Error(result.error || 'Login failed');
  };

  if (authLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900'}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Caricamento...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-300 ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900'}`}>
      <Header activeUser={activeUser} theme={theme} toggleTheme={toggleTheme} onLogout={signOut} />
      <main className="flex-grow container mx-auto px-4 py-8">
        {!activeUser ? (
          <LoginForm onLogin={handleLogin} theme={theme} />
        ) : activeUser.role === 'admin' ? (
          <AdminDashboard theme={theme} />
        ) : activeUser.role === 'agente' ? (
          <AgenteDashboard agenteId={activeUser.id} theme={theme} />
        ) : (
          <SegnalatoreDashboard segnalatoreId={activeUser.id} theme={theme} />
        )}
      </main>
      <Footer theme={theme} />
    </div>
  );
};

const Header = ({ activeUser, theme, toggleTheme, onLogout }) => (
  <header className={`border-b ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
    <div className="container mx-auto px-4 py-4 flex justify-between items-center">
      <div className="flex items-center space-x-3">
        <span className="text-2xl">🏠</span>
        <h1 className="text-xl font-bold text-blue-600">HomeScout</h1>
        {activeUser && (
          <span className={`text-sm px-2 py-1 rounded ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`}>
            {activeUser.role}
          </span>
        )}
      </div>
      <div className="flex items-center space-x-4">
        <button onClick={toggleTheme} className={`p-2 rounded-full ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}>
          {theme === 'dark' ? '🌞' : '🌙'}
        </button>
        {activeUser && (
          <button onClick={onLogout} className={`px-4 py-2 rounded ${theme === 'dark' ? 'bg-red-600 hover:bg-red-700' : 'bg-red-500 hover:bg-red-600 text-white'}`}>
            Logout
          </button>
        )}
      </div>
    </div>
  </header>
);

const Footer = ({ theme }) => (
  <footer className={`border-t ${theme === 'dark' ? 'bg-gray-800 border-gray-700 text-gray-300' : 'bg-white border-gray-200 text-gray-600'} py-6 mt-auto`}>
    <div className="container mx-auto px-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        {/* Brand */}
        <div className="flex items-center space-x-2">
          <span className="text-lg">🏠</span>
          <span className={`font-semibold text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>HomeScout</span>
        </div>

        {/* Legal Links */}
        <div className="flex flex-wrap items-center gap-4 text-xs">
          <a
            href="https://privacy-policy.homescout.dynamic-web.space"
            target="_blank"
            rel="noopener noreferrer"
            className={`hover:underline transition-colors duration-200 ${theme === 'dark' ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
          >
            Privacy Policy
          </a>
          <span className={theme === 'dark' ? 'text-gray-600' : 'text-gray-300'}>|</span>
          <a
            href="https://cookie-policy.homescout.dynamic-web.space"
            target="_blank"
            rel="noopener noreferrer"
            className={`hover:underline transition-colors duration-200 ${theme === 'dark' ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
          >
            Cookie Policy
          </a>
        </div>

        {/* Copyright */}
        <div className="text-xs text-center md:text-right space-y-1">
          <p className={`font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
            © 2025 Dynamic-Web Srls – P.Iva 03034040356
          </p>
          <p className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}>
            Tutti i diritti sono riservati
          </p>
        </div>
      </div>

      {/* Bottom line */}
      <div className={`mt-4 pt-4 border-t text-center text-xs ${theme === 'dark' ? 'border-gray-700 text-gray-500' : 'border-gray-100 text-gray-400'}`}>
        Realizzato da Dynamic-Web Srls · Archiviazione sicura dei dati con Supabase
      </div>
    </div>
  </footer>
);

const LoginForm = ({ onLogin, theme }) => {
  const [loginType, setLoginType] = useState('admin');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (loginType === 'admin' && password === '^h$W_J18xpE0SsL') {
        await onLogin('admin', '^h$W_J18xpE0SsL', 'admin');
      } else {
        await onLogin(username, password, loginType);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`max-w-md mx-auto p-6 rounded-lg shadow-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
      <h2 className="text-2xl font-bold mb-6 text-center">Accesso HomeScout</h2>
      <div className="flex mb-6 border-b">
        {['admin', 'agente', 'segnalatore'].map(type => (
          <button
            key={type}
            onClick={() => setLoginType(type)}
            className={`flex-1 py-2 text-sm font-medium ${loginType === type
              ? theme === 'dark' ? 'border-b-2 border-blue-500 text-blue-500' : 'border-b-2 border-blue-600 text-blue-600'
              : theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {type.charAt(0).toUpperCase() + type.slice(1)}
          </button>
        ))}
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        {loginType !== 'admin' && (
          <div>
            <label className="block text-sm font-medium mb-2">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className={`w-full p-3 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
              required
            />
          </div>
        )}
        <div>
          <label className="block text-sm font-medium mb-2">
            {loginType === 'admin' ? 'Password Admin' : 'Password'}
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={`w-full p-3 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
            required
          />
        </div>
        {loginType === 'admin' && (
          <div className={`text-xs p-2 rounded ${theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
            <p>🔐 Per motivi di sicurezza, la password admin non viene visualizzata.</p>
            <p className="mt-1">Contattare l'amministratore di sistema per le credenziali.</p>
          </div>
        )}
        {error && (
          <div className={`p-3 rounded ${theme === 'dark' ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-700'}`}>
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={loading}
          className={`w-full py-3 px-4 rounded font-medium ${theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600 text-white'} ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {loading ? 'Accesso in corso...' : 'Accedi'}
        </button>
      </form>
    </div>
  );
};

const AdminDashboard = ({ theme }) => {
  const [activeTab, setActiveTab] = useState('agenti');
  const { data: users, createItem: createUser, deleteItem: deleteUser, updateItem: updateUser, refetch: refetchUsers } = useSupabaseData('users');
  const { data: reports, deleteItem: deleteReport } = useSupabaseData('reports');
  const { data: news } = useSupabaseData('news');
  const [showAgentForm, setShowAgentForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');

  const agents = users?.filter(u => u.role === 'agente') || [];
  const reporters = users?.filter(u => u.role === 'segnalatore') || [];
  const tabs = [
    { id: 'agenti', label: 'Agenti', count: agents.length },
    { id: 'segnalatori', label: 'Segnalatori', count: reporters.length },
    { id: 'segnalazioni', label: 'Segnalazioni', count: reports?.length || 0 },
    { id: 'notizie', label: 'Notizie', count: news?.length || 0 }
  ];

  const handleAddAgent = async (agentData) => {
    setCreateError(''); setCreateSuccess('');
    try {
      const result = await createUser({ username: agentData.username, password_hash: hashPassword(agentData.password), role: 'agente' });
      if (result) {
        setCreateSuccess(`Agente "${agentData.username}" creato con successo!`);
        setShowAgentForm(false);
        setTimeout(() => refetchUsers(), 500);
      }
    } catch (error) { setCreateError(`Errore: ${error.message}`); }
  };

  const handleEditUser = async (userId, userData) => {
    setCreateError(''); setCreateSuccess('');
    try {
      const updates = { username: userData.username };
      if (userData.password?.trim()) updates.password_hash = hashPassword(userData.password);
      await updateUser(userId, updates);
      setCreateSuccess(`Utente "${userData.username}" aggiornato con successo!`);
      setEditingUser(null);
      setTimeout(() => refetchUsers(), 500);
    } catch (error) { setCreateError(`Errore: ${error.message}`); }
  };

  const handleDeleteUser = async (userId) => {
    try { await deleteUser(userId); setTimeout(() => refetchUsers(), 500); }
    catch (error) { console.error('Error deleting user:', error); }
  };

  const handleDeleteReport = async (reportId) => {
    try { await deleteReport(reportId); }
    catch (error) { console.error('Error deleting report:', error); }
  };

  return (
    <div>
      <h2 className="text-3xl font-bold mb-8">Dashboard Admin</h2>
      <div className="flex border-b mb-6">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`px-6 py-3 font-medium ${activeTab === tab.id
              ? theme === 'dark' ? 'border-b-2 border-blue-500 text-blue-500' : 'border-b-2 border-blue-600 text-blue-600'
              : theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}>
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {activeTab === 'agenti' && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold">Gestione Agenti</h3>
            <button onClick={() => setShowAgentForm(true)} className={`px-4 py-2 rounded ${theme === 'dark' ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600 text-white'}`}>
              + Nuovo Agente
            </button>
          </div>
          {createError && <div className={`mb-4 p-3 rounded ${theme === 'dark' ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-700'}`}>{createError}</div>}
          {createSuccess && <div className={`mb-4 p-3 rounded ${theme === 'dark' ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-700'}`}>{createSuccess}</div>}
          {showAgentForm && <UserForm theme={theme} onSubmit={handleAddAgent} onCancel={() => { setShowAgentForm(false); setCreateError(''); setCreateSuccess(''); }} />}
          {editingUser && editingUser.role === 'agente' && <UserForm theme={theme} user={editingUser} onSubmit={(data) => handleEditUser(editingUser.id, data)} onCancel={() => { setEditingUser(null); setCreateError(''); setCreateSuccess(''); }} />}
          <div className="grid gap-4">
            {agents.map(agent => (
              <div key={agent.id} className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white shadow'}`}>
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-bold text-lg">{agent.username}</h4>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Agente immobiliare</p>
                  </div>
                  <div className="flex space-x-2">
                    <button onClick={() => setEditingUser(agent)} className={`px-3 py-1 rounded ${theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}>Modifica</button>
                    <button onClick={() => handleDeleteUser(agent.id)} className={`px-3 py-1 rounded ${theme === 'dark' ? 'bg-red-600 hover:bg-red-700' : 'bg-red-500 hover:bg-red-600 text-white'}`}>Elimina</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'segnalatori' && (
        <div>
          <h3 className="text-2xl font-bold mb-6">Tutti i Segnalatori</h3>
          {createError && <div className={`mb-4 p-3 rounded ${theme === 'dark' ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-700'}`}>{createError}</div>}
          {createSuccess && <div className={`mb-4 p-3 rounded ${theme === 'dark' ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-700'}`}>{createSuccess}</div>}
          {editingUser && editingUser.role === 'segnalatore' && <UserForm theme={theme} user={editingUser} onSubmit={(data) => handleEditUser(editingUser.id, data)} onCancel={() => { setEditingUser(null); setCreateError(''); setCreateSuccess(''); }} />}
          <div className="grid gap-4">
            {reporters.map(reporter => {
              const agent = agents.find(a => a.id === reporter.agent_id);
              return (
                <div key={reporter.id} className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white shadow'}`}>
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-bold text-lg">{reporter.username}</h4>
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Agente: {agent?.username || 'N/A'}</p>
                    </div>
                    <div className="flex space-x-2">
                      <button onClick={() => setEditingUser(reporter)} className={`px-3 py-1 rounded ${theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}>Modifica</button>
                      <button onClick={() => handleDeleteUser(reporter.id)} className={`px-3 py-1 rounded ${theme === 'dark' ? 'bg-red-600 hover:bg-red-700' : 'bg-red-500 hover:bg-red-600 text-white'}`}>Elimina</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {activeTab === 'segnalazioni' && (
        <ReportsList reports={reports || []} reporters={reporters} agents={agents} theme={theme} canEdit={true} canDelete={true} deleteReport={handleDeleteReport} />
      )}
      {activeTab === 'notizie' && (
        <NewsList news={news || []} agents={agents} theme={theme} canEdit={true} />
      )}
    </div>
  );
};

const AgenteDashboard = ({ agenteId, theme }) => {
  const [activeTab, setActiveTab] = useState('segnalatori');
  const { data: users, createItem: createUser, deleteItem: deleteUser } = useSupabaseData('users');
  const { data: reports, updateItem: updateReport, deleteItem: deleteReport } = useSupabaseData('reports', { agentId: agenteId });
  const { data: news, createItem: createNews, updateItem: updateNews, deleteItem: deleteNews } = useSupabaseData('news', { agentId: agenteId });
  const [showReporterForm, setShowReporterForm] = useState(false);
  const [showNewsForm, setShowNewsForm] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [editingReport, setEditingReport] = useState(null);

  const myReporters = users?.filter(u => u.role === 'segnalatore' && u.agent_id === agenteId) || [];
  const tabs = [
    { id: 'segnalatori', label: 'Segnalatori', count: myReporters.length },
    { id: 'segnalazioni', label: 'Segnalazioni', count: reports?.length || 0 },
    { id: 'notizie', label: 'Notizie', count: news?.length || 0 }
  ];

  const handleAddReporter = async (data) => {
    try { await createUser({ username: data.username, password_hash: hashPassword(data.password), role: 'segnalatore', agent_id: agenteId }); setShowReporterForm(false); }
    catch (error) { console.error('Error creating reporter:', error); }
  };

  const handleAddNews = async (data) => {
    try { await createNews({ agent_id: agenteId, title: data.titolo, content: data.testo }); setShowNewsForm(false); }
    catch (error) { console.error('Error creating news:', error); }
  };

  const handleEditNews = async (newsId, data) => {
    try { await updateNews(newsId, { title: data.titolo, content: data.testo }); setEditingNews(null); }
    catch (error) { console.error('Error updating news:', error); }
  };

  const handleEditReport = async (reportId, data) => {
    try { await updateReport(reportId, data); setEditingReport(null); }
    catch (error) { console.error('Error updating report:', error); }
  };

  return (
    <div>
      <h2 className="text-3xl font-bold mb-8">Dashboard Agente</h2>
      <div className="flex border-b mb-6">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`px-6 py-3 font-medium ${activeTab === tab.id
              ? theme === 'dark' ? 'border-b-2 border-blue-500 text-blue-500' : 'border-b-2 border-blue-600 text-blue-600'
              : theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}>
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {activeTab === 'segnalatori' && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold">I miei Segnalatori</h3>
            <button onClick={() => setShowReporterForm(true)} className={`px-4 py-2 rounded ${theme === 'dark' ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600 text-white'}`}>+ Nuovo Segnalatore</button>
          </div>
          {showReporterForm && <AgentForm theme={theme} onSubmit={handleAddReporter} onCancel={() => setShowReporterForm(false)} />}
          <div className="grid gap-4">
            {myReporters.map(reporter => (
              <div key={reporter.id} className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white shadow'}`}>
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-bold text-lg">{reporter.username}</h4>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Segnalatore</p>
                  </div>
                  <button onClick={() => deleteUser(reporter.id)} className={`px-3 py-1 rounded ${theme === 'dark' ? 'bg-red-600 hover:bg-red-700' : 'bg-red-500 hover:bg-red-600 text-white'}`}>Elimina</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'segnalazioni' && (
        <div>
          {editingReport && <ReportStatusForm theme={theme} report={editingReport} onSubmit={(data) => handleEditReport(editingReport.id, data)} onCancel={() => setEditingReport(null)} />}
          <ReportsList reports={reports || []} reporters={myReporters} theme={theme} canEdit={true} canDelete={true} updateReport={updateReport} deleteReport={(id) => deleteReport(id)} onEditReport={setEditingReport} />
        </div>
      )}

      {activeTab === 'notizie' && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold">Le mie Notizie</h3>
            <button onClick={() => setShowNewsForm(true)} className={`px-4 py-2 rounded ${theme === 'dark' ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600 text-white'}`}>+ Nuova Notizia</button>
          </div>
          {(showNewsForm || editingNews) && (
            <NewsForm theme={theme} news={editingNews}
              onSubmit={editingNews ? (data) => handleEditNews(editingNews.id, data) : handleAddNews}
              onCancel={() => { setShowNewsForm(false); setEditingNews(null); }} />
          )}
          <div className="grid gap-4">
            {news?.map(item => (
              <div key={item.id} className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white shadow'}`}>
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-bold text-lg">{item.title}</h4>
                  <div className="flex space-x-2">
                    <button onClick={() => setEditingNews(item)} className={`px-3 py-1 rounded ${theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}>Modifica</button>
                    <button onClick={() => deleteNews(item.id)} className={`px-3 py-1 rounded ${theme === 'dark' ? 'bg-red-600 hover:bg-red-700' : 'bg-red-500 hover:bg-red-600 text-white'}`}>Elimina</button>
                  </div>
                </div>
                <p className={`mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>{item.content}</p>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{new Date(item.created_at).toLocaleDateString()}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const SegnalatoreDashboard = ({ segnalatoreId, theme }) => {
  const [activeTab, setActiveTab] = useState('segnalazioni');
  const { data: reports, createItem: createReport } = useSupabaseData('reports', { userId: segnalatoreId });
  const { data: news } = useSupabaseData('news');
  const [showReportForm, setShowReportForm] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const { data: users } = useSupabaseData('users');
  const [reportError, setReportError] = useState('');
  const [reportSuccess, setReportSuccess] = useState('');

  const myReporter = users?.find(u => u.id === segnalatoreId);
  const myNews = news?.filter(n => n.agent_id === myReporter?.agent_id) || [];
  const tabs = [
    { id: 'segnalazioni', label: 'Le mie Segnalazioni', count: reports?.length || 0 },
    { id: 'notizie', label: 'Notizie', count: myNews.length }
  ];

  const handleAddReport = async (reportData) => {
    setReportError(''); setReportSuccess('');
    try {
      if (!myReporter?.agent_id) throw new Error('Segnalatore non assegnato ad un agente');
      await createReport({
        user_id: segnalatoreId, agent_id: myReporter.agent_id,
        address: reportData.indirizzo, sqm: parseInt(reportData.mq),
        property_type: reportData.tipo, condition: reportData.stato,
        owner_name: reportData.nome_proprietario, owner_phone: reportData.telefono_proprietario,
        notes: reportData.note, status: 'In attesa', photos: reportData.foto || []
      });
      setReportSuccess('Segnalazione creata con successo!');
      setShowReportForm(false);
      setTimeout(() => setReportSuccess(''), 3000);
    } catch (error) { setReportError(`Errore: ${error.message}`); }
  };

  return (
    <div>
      <h2 className="text-3xl font-bold mb-8">Dashboard Segnalatore</h2>
      <div className="flex border-b mb-6">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`px-6 py-3 font-medium ${activeTab === tab.id
              ? theme === 'dark' ? 'border-b-2 border-blue-500 text-blue-500' : 'border-b-2 border-blue-600 text-blue-600'
              : theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}>
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {activeTab === 'segnalazioni' && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold">Le mie Segnalazioni</h3>
            <button onClick={() => setShowReportForm(true)} className={`px-4 py-2 rounded ${theme === 'dark' ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600 text-white'}`}>+ Nuova Segnalazione</button>
          </div>
          {reportError && <div className={`mb-4 p-3 rounded ${theme === 'dark' ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-700'}`}>{reportError}</div>}
          {reportSuccess && <div className={`mb-4 p-3 rounded ${theme === 'dark' ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-700'}`}>{reportSuccess}</div>}
          {showReportForm && <ReportForm theme={theme} onSubmit={handleAddReport} onCancel={() => { setShowReportForm(false); setReportError(''); setReportSuccess(''); }} />}
          <div className="grid gap-4">
            {reports?.map(report => (
              <div key={report.id} className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white shadow'}`}>
                <div className="flex justify-between items-center mb-3">
                  <div>
                    <h4 className="font-bold text-lg">{report.address}</h4>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{report.sqm}m² - {report.property_type}</p>
                  </div>
                  <button onClick={() => setSelectedReport(report)} className={`px-3 py-1 rounded ${theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}>Dettagli</button>
                </div>
                <div className={`inline-block px-2 py-1 rounded text-sm text-white ${report.status === 'Venduto' ? 'bg-green-500' : report.status === 'Incarico preso' ? 'bg-blue-500' : report.status === 'Visionato' ? 'bg-yellow-500' : report.status === 'Rifiutato' ? 'bg-red-500' : 'bg-gray-500'}`}>
                  {report.status}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {activeTab === 'notizie' && <NewsList news={myNews} theme={theme} canEdit={false} />}
      {selectedReport && <ReportModal report={selectedReport} theme={theme} onClose={() => setSelectedReport(null)} />}
    </div>
  );
};

const UserForm = ({ theme, user, onSubmit, onCancel }) => {
  const [username, setUsername] = useState(user?.username || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password && password !== confirmPassword) { alert('Le password non coincidono'); return; }
    const userData = { username };
    if (password?.trim()) userData.password = password;
    onSubmit(userData);
    setUsername(''); setPassword(''); setConfirmPassword('');
  };

  return (
    <div className={`p-6 rounded-lg mb-6 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white shadow'}`}>
      <h3 className="text-xl font-bold mb-4">{user ? `Modifica ${user.role === 'agente' ? 'Agente' : 'Segnalatore'}` : 'Nuovo Agente'}</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">Username</label>
          <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} className={`w-full p-3 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">{user ? 'Nuova Password (lascia vuoto per non cambiare)' : 'Password'}</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={`w-full p-3 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`} required={!user} />
        </div>
        {password && (
          <div>
            <label className="block text-sm font-medium mb-2">Conferma Password</label>
            <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className={`w-full p-3 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`} required />
          </div>
        )}
        <div className="flex space-x-3">
          <button type="submit" className={`px-4 py-2 rounded ${theme === 'dark' ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600 text-white'}`}>{user ? 'Aggiorna' : 'Crea'}</button>
          <button type="button" onClick={onCancel} className={`px-4 py-2 rounded ${theme === 'dark' ? 'bg-gray-600 hover:bg-gray-700' : 'bg-gray-300 hover:bg-gray-400'}`}>Annulla</button>
        </div>
      </form>
    </div>
  );
};

const AgentForm = ({ theme, onSubmit, onCancel }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const handleSubmit = (e) => { e.preventDefault(); onSubmit({ username, password }); setUsername(''); setPassword(''); };
  return (
    <div className={`p-6 rounded-lg mb-6 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white shadow'}`}>
      <h3 className="text-xl font-bold mb-4">Nuovo Utente</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">Username</label>
          <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} className={`w-full p-3 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Password</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={`w-full p-3 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`} required />
        </div>
        <div className="flex space-x-3">
          <button type="submit" className={`px-4 py-2 rounded ${theme === 'dark' ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600 text-white'}`}>Crea</button>
          <button type="button" onClick={onCancel} className={`px-4 py-2 rounded ${theme === 'dark' ? 'bg-gray-600 hover:bg-gray-700' : 'bg-gray-300 hover:bg-gray-400'}`}>Annulla</button>
        </div>
      </form>
    </div>
  );
};

const ReportForm = ({ theme, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({ indirizzo: '', mq: '', tipo: 'Appartamento', stato: 'Nuovo', nome_proprietario: '', telefono_proprietario: '', note: '', foto: [] });
  const handleSubmit = (e) => { e.preventDefault(); onSubmit(formData); };
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files).slice(0, 3 - formData.foto.length);
    files.forEach(file => { const reader = new FileReader(); reader.onload = (e) => setFormData(prev => ({ ...prev, foto: [...prev.foto, e.target.result] })); reader.readAsDataURL(file); });
  };
  const removeImage = (index) => setFormData(prev => ({ ...prev, foto: prev.foto.filter((_, i) => i !== index) }));

  return (
    <div className={`p-6 rounded-lg mb-6 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white shadow'}`}>
      <h3 className="text-xl font-bold mb-4">Nuova Segnalazione</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium mb-2">Indirizzo *</label><input type="text" value={formData.indirizzo} onChange={(e) => setFormData({ ...formData, indirizzo: e.target.value })} className={`w-full p-3 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`} required /></div>
          <div><label className="block text-sm font-medium mb-2">Metri Quadri *</label><input type="number" value={formData.mq} onChange={(e) => setFormData({ ...formData, mq: e.target.value })} className={`w-full p-3 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`} required /></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium mb-2">Tipo Immobile *</label>
            <select value={formData.tipo} onChange={(e) => setFormData({ ...formData, tipo: e.target.value })} className={`w-full p-3 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}>
              <option>Appartamento</option><option>Villa</option><option>Loft</option><option>Casa Indipendente</option><option>Attico</option>
            </select></div>
          <div><label className="block text-sm font-medium mb-2">Stato Immobile *</label>
            <select value={formData.stato} onChange={(e) => setFormData({ ...formData, stato: e.target.value })} className={`w-full p-3 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}>
              <option>Nuovo</option><option>Ristrutturato</option><option>Da ristrutturare</option><option>Ottimo</option><option>Buono</option>
            </select></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium mb-2">Proprietario *</label><input type="text" value={formData.nome_proprietario} onChange={(e) => setFormData({ ...formData, nome_proprietario: e.target.value })} className={`w-full p-3 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`} required /></div>
          <div><label className="block text-sm font-medium mb-2">Telefono *</label><input type="tel" value={formData.telefono_proprietario} onChange={(e) => setFormData({ ...formData, telefono_proprietario: e.target.value })} className={`w-full p-3 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`} required /></div>
        </div>
        <div><label className="block text-sm font-medium mb-2">Note</label><textarea value={formData.note} onChange={(e) => setFormData({ ...formData, note: e.target.value })} rows={3} className={`w-full p-3 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`} /></div>
        <div>
          <label className="block text-sm font-medium mb-2">Foto (max 3)</label>
          <div className="flex flex-wrap gap-4 mb-3">
            {formData.foto.map((foto, index) => (
              <div key={index} className="relative">
                <img src={foto} alt={`Anteprima ${index + 1}`} className="w-20 h-20 object-cover rounded" />
                <button type="button" onClick={() => removeImage(index)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">×</button>
              </div>
            ))}
          </div>
          {formData.foto.length < 3 && <input type="file" accept="image/*" multiple onChange={handleImageUpload} className={`w-full p-3 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`} />}
        </div>
        <div className="flex space-x-3">
          <button type="submit" className={`px-4 py-2 rounded ${theme === 'dark' ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600 text-white'}`}>Crea Segnalazione</button>
          <button type="button" onClick={onCancel} className={`px-4 py-2 rounded ${theme === 'dark' ? 'bg-gray-600 hover:bg-gray-700' : 'bg-gray-300 hover:bg-gray-400'}`}>Annulla</button>
        </div>
      </form>
    </div>
  );
};

const ReportStatusForm = ({ theme, report, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({ address: report?.address || '', sqm: report?.sqm || '', property_type: report?.property_type || 'Appartamento', condition: report?.condition || 'Nuovo', owner_name: report?.owner_name || '', owner_phone: report?.owner_phone || '', notes: report?.notes || '', status: report?.status || 'In attesa', photos: report?.photos || [] });
  const handleSubmit = (e) => { e.preventDefault(); onSubmit(formData); };
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files).slice(0, 3 - formData.photos.length);
    files.forEach(file => { const reader = new FileReader(); reader.onload = (e) => setFormData(prev => ({ ...prev, photos: [...prev.photos, e.target.result] })); reader.readAsDataURL(file); });
  };
  const removeImage = (index) => setFormData(prev => ({ ...prev, photos: prev.photos.filter((_, i) => i !== index) }));

  return (
    <div className={`p-6 rounded-lg mb-6 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white shadow'}`}>
      <h3 className="text-xl font-bold mb-4">Modifica Segnalazione</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium mb-2">Indirizzo *</label><input type="text" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className={`w-full p-3 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`} required /></div>
          <div><label className="block text-sm font-medium mb-2">Metri Quadri *</label><input type="number" value={formData.sqm} onChange={(e) => setFormData({ ...formData, sqm: e.target.value })} className={`w-full p-3 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`} required /></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium mb-2">Tipo Immobile *</label>
            <select value={formData.property_type} onChange={(e) => setFormData({ ...formData, property_type: e.target.value })} className={`w-full p-3 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}>
              <option>Appartamento</option><option>Villa</option><option>Loft</option><option>Casa Indipendente</option><option>Attico</option>
            </select></div>
          <div><label className="block text-sm font-medium mb-2">Stato Immobile *</label>
            <select value={formData.condition} onChange={(e) => setFormData({ ...formData, condition: e.target.value })} className={`w-full p-3 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}>
              <option>Nuovo</option><option>Ristrutturato</option><option>Da ristrutturare</option><option>Ottimo</option><option>Buono</option>
            </select></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium mb-2">Proprietario *</label><input type="text" value={formData.owner_name} onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })} className={`w-full p-3 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`} required /></div>
          <div><label className="block text-sm font-medium mb-2">Telefono *</label><input type="tel" value={formData.owner_phone} onChange={(e) => setFormData({ ...formData, owner_phone: e.target.value })} className={`w-full p-3 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`} required /></div>
        </div>
        <div><label className="block text-sm font-medium mb-2">Stato Segnalazione *</label>
          <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className={`w-full p-3 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}>
            <option>In attesa</option><option>Visionato</option><option>Incarico preso</option><option>Venduto</option><option>Rifiutato</option>
          </select></div>
        <div><label className="block text-sm font-medium mb-2">Note</label><textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} className={`w-full p-3 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`} /></div>
        <div>
          <label className="block text-sm font-medium mb-2">Foto (max 3)</label>
          <div className="flex flex-wrap gap-4 mb-3">
            {formData.photos.map((foto, index) => (
              <div key={index} className="relative">
                <img src={foto} alt={`Immobile ${index + 1}`} className="w-20 h-20 object-cover rounded" />
                <button type="button" onClick={() => removeImage(index)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">×</button>
              </div>
            ))}
          </div>
          {formData.photos.length < 3 && <input type="file" accept="image/*" multiple onChange={handleImageUpload} className={`w-full p-3 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`} />}
        </div>
        <div className="flex space-x-3">
          <button type="submit" className={`px-4 py-2 rounded ${theme === 'dark' ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600 text-white'}`}>Aggiorna Segnalazione</button>
          <button type="button" onClick={onCancel} className={`px-4 py-2 rounded ${theme === 'dark' ? 'bg-gray-600 hover:bg-gray-700' : 'bg-gray-300 hover:bg-gray-400'}`}>Annulla</button>
        </div>
      </form>
    </div>
  );
};

const NewsForm = ({ theme, news, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({ titolo: news?.title || '', testo: news?.content || '' });
  const handleSubmit = (e) => { e.preventDefault(); onSubmit(formData); };
  return (
    <div className={`p-6 rounded-lg mb-6 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white shadow'}`}>
      <h3 className="text-xl font-bold mb-4">{news ? 'Modifica Notizia' : 'Nuova Notizia'}</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div><label className="block text-sm font-medium mb-2">Titolo *</label><input type="text" value={formData.titolo} onChange={(e) => setFormData({ ...formData, titolo: e.target.value })} className={`w-full p-3 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`} required /></div>
        <div><label className="block text-sm font-medium mb-2">Testo *</label><textarea value={formData.testo} onChange={(e) => setFormData({ ...formData, testo: e.target.value })} rows={5} className={`w-full p-3 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`} required /></div>
        <div className="flex space-x-3">
          <button type="submit" className={`px-4 py-2 rounded ${theme === 'dark' ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600 text-white'}`}>{news ? 'Aggiorna' : 'Pubblica'} Notizia</button>
          <button type="button" onClick={onCancel} className={`px-4 py-2 rounded ${theme === 'dark' ? 'bg-gray-600 hover:bg-gray-700' : 'bg-gray-300 hover:bg-gray-400'}`}>Annulla</button>
        </div>
      </form>
    </div>
  );
};

const ReportsList = ({ reports, reporters, agents, theme, canEdit, canDelete, updateReport, deleteReport, onEditReport }) => {
  const [selectedReport, setSelectedReport] = useState(null);
  const [statusFilter, setStatusFilter] = useState('Tutti');
  const filteredReports = statusFilter === 'Tutti' ? reports : reports.filter(r => r.status === statusFilter);
  const getReporterName = (userId) => reporters?.find(r => r.id === userId)?.username || 'N/A';

  const handleDeleteReport = async (reportId) => {
    if (window.confirm('Sei sicuro di voler eliminare questa segnalazione?')) {
      try { if (deleteReport) await deleteReport(reportId); }
      catch (error) { console.error('Error deleting report:', error); }
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-2xl font-bold">Segnalazioni</h3>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={`p-2 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}>
          <option value="Tutti">Tutti gli stati</option>
          <option>In attesa</option><option>Visionato</option><option>Incarico preso</option><option>Venduto</option><option>Rifiutato</option>
        </select>
      </div>
      <div className="grid gap-4">
        {filteredReports.map(report => (
          <div key={report.id} className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white shadow'}`}>
            <div className="flex justify-between items-center mb-3">
              <div>
                <h4 className="font-bold text-lg">{report.address}</h4>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{report.sqm}m² - {report.property_type} - {report.owner_name}</p>
                {reporters && <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>Segnalatore: {getReporterName(report.user_id)}</p>}
              </div>
              <div className="flex space-x-2">
                <button onClick={() => setSelectedReport(report)} className={`px-3 py-1 rounded ${theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}>Dettagli</button>
                {canEdit && <button onClick={() => onEditReport && onEditReport(report)} className={`px-3 py-1 rounded ${theme === 'dark' ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-yellow-500 hover:bg-yellow-600 text-white'}`}>Modifica</button>}
                {canDelete && <button onClick={() => handleDeleteReport(report.id)} className={`px-3 py-1 rounded ${theme === 'dark' ? 'bg-red-600 hover:bg-red-700' : 'bg-red-500 hover:bg-red-600 text-white'}`}>Elimina</button>}
              </div>
            </div>
            <div className={`inline-block px-2 py-1 rounded text-sm text-white ${report.status === 'Venduto' ? 'bg-green-500' : report.status === 'Incarico preso' ? 'bg-blue-500' : report.status === 'Visionato' ? 'bg-yellow-500' : report.status === 'Rifiutato' ? 'bg-red-500' : 'bg-gray-500'}`}>
              {report.status}
            </div>
          </div>
        ))}
      </div>
      {selectedReport && <ReportModal report={selectedReport} theme={theme} onClose={() => setSelectedReport(null)} />}
    </div>
  );
};

const NewsList = ({ news, agents, theme, canEdit }) => (
  <div>
    <h3 className="text-2xl font-bold mb-6">Notizie</h3>
    <div className="grid gap-4">
      {news.map(item => {
        const agentName = agents?.find(a => a.id === item.agent_id)?.username || 'N/A';
        return (
          <div key={item.id} className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white shadow'}`}>
            <div className="flex justify-between items-center mb-3">
              <div>
                <h4 className="font-bold text-lg">{item.title}</h4>
                {agents && <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Pubblicato da: {agentName}</p>}
              </div>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{new Date(item.created_at).toLocaleDateString()}</p>
            </div>
            <p className={`mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>{item.content}</p>
          </div>
        );
      })}
    </div>
  </div>
);

const ReportModal = ({ report, theme, onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
    <div className={`max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold">Dettagli Segnalazione</h3>
          <button onClick={onClose} className={`p-2 rounded-full ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}>×</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <h4 className="font-bold mb-2">Informazioni Immobile</h4>
            <div className="space-y-2">
              <p><strong>Indirizzo:</strong> {report.address}</p>
              <p><strong>Metri Quadri:</strong> {report.sqm}m²</p>
              <p><strong>Tipo:</strong> {report.property_type}</p>
              <p><strong>Stato Immobile:</strong> {report.condition}</p>
            </div>
          </div>
          <div>
            <h4 className="font-bold mb-2">Proprietario</h4>
            <div className="space-y-2">
              <p><strong>Nome:</strong> {report.owner_name}</p>
              <p><strong>Telefono:</strong> {report.owner_phone}</p>
            </div>
          </div>
        </div>
        <div className="mb-6">
          <h4 className="font-bold mb-2">Note</h4>
          <p className={`p-3 rounded ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>{report.notes || 'Nessuna nota'}</p>
        </div>
        <div className="mb-6">
          <h4 className="font-bold mb-2">Stato Segnalazione</h4>
          <div className={`inline-block px-3 py-1 rounded text-white ${report.status === 'Venduto' ? 'bg-green-500' : report.status === 'Incarico preso' ? 'bg-blue-500' : report.status === 'Visionato' ? 'bg-yellow-500' : report.status === 'Rifiutato' ? 'bg-red-500' : 'bg-gray-500'}`}>
            {report.status}
          </div>
        </div>
        {report.photos?.length > 0 && (
          <div>
            <h4 className="font-bold mb-2">Foto</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {report.photos.map((foto, index) => <img key={index} src={foto} alt={`Immobile ${index + 1}`} className="w-full h-48 object-cover rounded" />)}
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
);

export default App;